import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { PlusOutlined } from '@ant-design/icons';
import {
  AdditionalFileModal,
  DataTable,
  AuthorizedButton,
  PageTitle,
  Container,
  GroupHealthInsuranceDependentsCancelModal,
  SExportButton,
  GroupHealthInsuranceDependentsModal,
} from '../../../../../components';
import { tableActions } from '../../../../../store/reducers/tableReducer';
import styles from './index.module.scss';
import { INITIAL_VALUES } from './const';
import { useAppDispatch, useAppSelector } from '../../../../../store';
import useHttpService from '../../../../../services/hook';
import { getColumns } from './columns';
import validationSchema from './validations';
import { IExpandedRow } from '../../../../../components/DataTable/type';
import { IEnum } from '../../../../../services/type';
import {
  IFile,
  IWithViewPerm,
  IGroupHealthInsuranceDependentsDataSource,
  IGroupHealthInsuranceDependentsValues,
} from '../../../../../types';
import { Flex } from 'antd';
import { modalTypes } from '../../../../../assets/enums';
import { downloadFile } from '../../../../../helpers/File';
import {
  cleanupAfterSubmit,
  prepareModalData,
} from '../../../../../utils/functions';
import useFetchParameters from '../../../../../hooks';
import GroupHealthInsuranceDependentsService from '../../../../../api-clients/GroupHealthInsuranceDependentsService';
import EnumService from '../../../../../api-clients/EnumService';
import AuthService from '../../../../../api-clients/AuthService';

const Individuals: FC = () => {
  const dispatch = useAppDispatch();
  const executeService = useHttpService();
  const organizations = useAppSelector(
    (state) => state.organizationReducer.organizations,
  );

  // Redux State
  const search = useAppSelector((state) => state.tableReducer.search);
  const sort = useAppSelector((state) => state.tableReducer.sort);
  const page = useAppSelector((state) => state.tableReducer.page);

  // State
  const [selectedItem, setSelectedItem] = useState<
    IGroupHealthInsuranceDependentsDataSource | undefined
  >(undefined);
  const [additionalFile, setAdditionalFile] = useState<IFile[] | undefined>(
    undefined,
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<
    IGroupHealthInsuranceDependentsDataSource[]
  >([]);
  const [cancelledItem, setCancelledItem] = useState<
    IGroupHealthInsuranceDependentsDataSource | undefined
  >(undefined);
  const [expandedRows, setExpandedRows] = useState<IExpandedRow[]>([]);
  const [statusChoices, setStatusChoices] = useState<IEnum[]>([]);
  const [policyTypes, setPolicyTypes] = useState<IEnum[]>([]);
  const [dependentPayments, setDependentPayments] = useState<IEnum[]>([]);
  const [dateOfTermination, setDateOfTermination] = useState<string>('');
  const [additionalFileID, setAdditionalFileID] = useState<number | undefined>(
    undefined,
  );
  const [tempAdditionalFiles, setTempAdditionalFiles] = useState<
    FileList | undefined
  >(undefined);
  const [withViewPerm, setWithViewPerm] = useState<IWithViewPerm[]>([]);
  const [peopleForOrg, setPeopleForOrg] = useState<IEnum[]>([]);

  const parameters = useFetchParameters({ search, sort, page });
  const groupHealthInsuranceDependentsService = useMemo(
    () => new GroupHealthInsuranceDependentsService(executeService, parameters),
    [executeService, parameters],
  );
  const authService = useMemo(
    () => new AuthService(executeService),
    [executeService],
  );
  const enumService = useMemo(
    () => new EnumService(executeService),
    [executeService],
  );

  // Handlers
  const handleFetchWithViewPerm = async () => {
    const response = await authService.fetchWithViewPerm(
      'grouphealthdependent',
      formik.values.organization,
    );
    if (response && !('statusText' in response)) {
      setWithViewPerm(response.results);
    }
  };

  const handleFetchPeopleForOrg = async (id: number) => {
    const response =
      await groupHealthInsuranceDependentsService.fetchPeopleForOrg(id);
    if (response && Array.isArray(response)) {
      const result: IEnum[] = response.map((item) => ({
        key: item.id.toString(),
        value: item.name,
      }));

      setPeopleForOrg(result);
    }
  };

  const handleFetchEnums = async () => {
    const statusChoices = await enumService.fetchPoliciesStatusChoices();
    if (statusChoices && Array.isArray(statusChoices)) {
      setStatusChoices(statusChoices);
    }

    const policyTypes =
      await enumService.fetchGroupHealthInsurancePolicyTypes();
    if (policyTypes && Array.isArray(policyTypes)) {
      setPolicyTypes(policyTypes);
    }

    const dependentPayments = await enumService.fetchDependentPayments();
    if (dependentPayments && Array.isArray(dependentPayments)) {
      setDependentPayments(dependentPayments);
    }
  };

  const handleFetchData = async () => {
    const response = await groupHealthInsuranceDependentsService.fetchData();
    if (response && 'results' in response) {
      dispatch(tableActions.changeTableTotal(response.count));
      const results = response.results.reduce<
        IGroupHealthInsuranceDependentsDataSource[]
      >(
        (
          prev: IGroupHealthInsuranceDependentsDataSource[],
          cur: IGroupHealthInsuranceDependentsDataSource,
        ) => {
          return [...prev, { ...cur, key: cur.id }];
        },
        [],
      );
      setDataSource(results);
      if (additionalFileID) {
        const item = results.find((item) => item.id === additionalFileID);
        if (item) {
          setAdditionalFileID(item.id);
          setAdditionalFile(item.additional_files);
        }
      }
    }
  };

  const handleClickEye = async (id: number) => {
    dispatch(tableActions.changeTableRow(id));

    const expandedRows =
      await groupHealthInsuranceDependentsService.fetchExpandedRows(id);
    if (expandedRows && Array.isArray(expandedRows)) {
      setExpandedRows(expandedRows);
    }
  };

  const handleOpenModal = () => setIsModalOpen(true);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleOpenEditModal = async (id: number) => {
    const selectedItem =
      await groupHealthInsuranceDependentsService.fetchItemById(id);
    if (selectedItem && 'id' in selectedItem) {
      setSelectedItem(selectedItem);
    }
  };

  const handleCloseEditModal = () => {
    setSelectedItem(undefined);
    formik.setErrors({});
    formik.setTouched({});
  };

  const handleUploadFile = (e: ChangeEvent<HTMLInputElement>) =>
    formik.setFieldValue('upload_files', e.target.files);

  const handleClickDelete = async (id: number) => {
    try {
      await groupHealthInsuranceDependentsService.deleteItemById(id);
    } finally {
      cleanupAfterSubmit<IGroupHealthInsuranceDependentsDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
    }
  };

  const handleClickExport = async () => {
    const response = await groupHealthInsuranceDependentsService.exportData();
    if (response && 'download_url' in response) {
      downloadFile(response.download_url);
    }
  };

  const handleSubmit = async (
    values: IGroupHealthInsuranceDependentsValues,
  ) => {
    try {
      await groupHealthInsuranceDependentsService.submitForm(
        values,
        selectedItem,
      );

      if (selectedItem) {
        await groupHealthInsuranceDependentsService.updateStatus(
          selectedItem.id,
          values.recent_status,
          values.email_users || [],
        );
      }
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IGroupHealthInsuranceDependentsDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
      dispatch(tableActions.changeTablePage({ number: 0, size: 20 }));
    }
  };

  const handleChangeCancelledItem = (
    item: IGroupHealthInsuranceDependentsDataSource,
  ) => {
    setCancelledItem(item);
  };

  const handleSubmitCancelModal = async () => {
    try {
      if (cancelledItem && dateOfTermination.length) {
        await groupHealthInsuranceDependentsService.cancelDependent(
          cancelledItem.id,
          dateOfTermination,
        );
        setCancelledItem(undefined);
      }
    } catch (e) {
      console.log(e);
    } finally {
      cleanupAfterSubmit<IGroupHealthInsuranceDependentsDataSource>(
        setIsModalOpen,
        setSelectedItem,
        formik.resetForm,
        handleFetchData,
      );
      dispatch(tableActions.changeTablePage({ number: 0, size: 20 }));
    }
  };

  const handleCloseCancelModal = () => {
    setCancelledItem(undefined);
  };

  const handleSetAdditionalFile = (id: number, file: IFile[]) => {
    setAdditionalFileID(id);
    setAdditionalFile(file);
  };

  const handleUploadAdditionalFile = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setTempAdditionalFiles(e.target.files);
    }
  };

  const handleCloseAdditionalFile = () => {
    setAdditionalFileID(undefined);
    setAdditionalFile(undefined);
    setTempAdditionalFiles(undefined);
  };

  const handleDeleteFile = async (id: number, fileId: number) => {
    await groupHealthInsuranceDependentsService.deleteFile(id, fileId);
    handleFetchData();
  };

  const handleSubmitFile = async () => {
    if (additionalFileID && tempAdditionalFiles) {
      await groupHealthInsuranceDependentsService.uploadFiles(
        additionalFileID,
        tempAdditionalFiles,
      );
      setTempAdditionalFiles(undefined);
      handleFetchData();
    }
  };

  // Columns
  const columns = getColumns(
    handleClickEye,
    handleOpenEditModal,
    handleClickDelete,
    handleChangeCancelledItem,
    handleSetAdditionalFile,
    organizations,
    statusChoices,
    policyTypes,
    dependentPayments,
  );

  // Form Management
  const formik = useFormik<IGroupHealthInsuranceDependentsValues>({
    initialValues: INITIAL_VALUES,
    validationSchema,
    onSubmit: handleSubmit,
  });

  // Effects
  useEffect(() => {
    handleFetchEnums();
  }, []);

  useEffect(() => {
    if (formik.values.organization) {
      handleFetchWithViewPerm();
      handleFetchPeopleForOrg(formik.values.organization);
    }
  }, [formik.values.organization]);

  useEffect(() => {
    if (selectedItem) {
      const result = prepareModalData<
        IGroupHealthInsuranceDependentsDataSource,
        IGroupHealthInsuranceDependentsValues
      >(selectedItem);
      result && formik.setValues(result);
    } else {
      formik.setValues(INITIAL_VALUES);
    }
  }, [selectedItem]);

  useEffect(() => {
    handleFetchData();
  }, [search, sort.key, sort.order, page.number, page.size]);

  // JSX Return
  return (
    <Container>
      <PageTitle title="Grup Sağlık Sigortası - Bağımlılar" />
      <Flex vertical gap="middle">
        <Flex justify="flex-end" gap={5}>
          <SExportButton onClick={handleClickExport} />
          <AuthorizedButton
            model="grouphealthdependent"
            action="add"
            className={styles.button}
            onClick={handleOpenModal}
          >
            <PlusOutlined className={styles.buttonIcon} />
            <div className={styles.buttonText}>Yeni Bağımlı Ekleme Talebi</div>
          </AuthorizedButton>
        </Flex>
        <DataTable<IGroupHealthInsuranceDependentsDataSource>
          scrollable
          columns={columns}
          dataSource={dataSource}
          expandedRows={expandedRows}
          statusChoices={statusChoices}
        />
      </Flex>
      <GroupHealthInsuranceDependentsModal
        type={modalTypes.CREATE}
        open={isModalOpen}
        title="Grup Sağlık Yeni Bağımlı Ekleme Çıkarma Talebi"
        subTitle="Grup Sağlık Sigortası İçin Yeni Bağımlı Ekleme Çıkarma Talebi"
        formik={formik}
        organizations={organizations}
        dependentPayments={dependentPayments}
        handleUploadFile={handleUploadFile}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseModal}
        statusChoices={statusChoices}
        withViewPerm={withViewPerm}
        peopleForOrg={peopleForOrg}
      />
      <GroupHealthInsuranceDependentsModal
        type={modalTypes.EDIT}
        open={!!selectedItem}
        title="Grup Sağlık Bağımlılar Güncelleme"
        subTitle="Grup Sağlık Sigortası İçin Bağımlılar Güncelleme"
        formik={formik}
        organizations={organizations}
        dependentPayments={dependentPayments}
        handleUploadFile={handleUploadFile}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseEditModal}
        statusChoices={statusChoices}
        withViewPerm={withViewPerm}
        peopleForOrg={peopleForOrg}
      />
      <GroupHealthInsuranceDependentsCancelModal
        open={!!cancelledItem}
        title="Sigortalı İptal Talebi"
        subTitle="Grup Sağlık Sigortası İçin Sigortalı İptal Talebi Oluşturma"
        item={cancelledItem}
        organizations={organizations}
        setDateOfTermination={setDateOfTermination}
        handleSubmit={handleSubmitCancelModal}
        handleCancel={handleCloseCancelModal}
      />
      <AdditionalFileModal
        open={!!additionalFileID && Array.isArray(additionalFile)}
        model="grouphealthdependentadditionalfile"
        tempAdditionalFiles={tempAdditionalFiles}
        additionalFiles={additionalFile}
        handleUploadFile={handleUploadAdditionalFile}
        handleDeleteFile={handleDeleteFile}
        handleSubmit={handleSubmitFile}
        handleCancel={handleCloseAdditionalFile}
      />
    </Container>
  );
};

export default Individuals;
